import { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, Grid2, Button, Box, InputAdornment, ToggleButton, Fade } from '@mui/material';

import Layout from 'components/Layout';
import { PreviewExperience, Title, BasicTable, PaginationControlled, CircularIndeterminate, useDebounce, ExportForm, AlertDialog, DateFormattedUtils, MoneyFormattedUtils, LookUpContract, LookUpExperience, LookUpExperiencer, LookUpHost, DateRangePickerWithInput, TextInput, SelectCheckbox, Chip, PreviewExperienceDetail } from '@utriper/core-ui';
import { BookingStatus, BookingOrigin } from '@utriper/core';

import { useBooking } from 'core/contexts/booking';
import { PreviewService } from 'core/api/services/preview';
import { DataportService } from 'core/api/services/dataport';

import { StyledToggleButton, StyledToggleButtonGroup, useStyles } from './styles';
import { Field, Form } from 'react-final-form';
import { Download, FilterList, RestartAlt, Search } from '@mui/icons-material';
import { EntityService } from 'core/api/services/lookUp';
import ResponsivePanel from 'components/ResponsivePanel';

const BookingsPage = () => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { t: translate, i18n } = useTranslation();
    const i18nLang = i18n.language;

    const { getBookings, bookings, loading, total, bookingCounts } = useBooking();

    const classes = useStyles();
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);

    const [searchFilter, setSearchFilter] = useState('');
    const searchTermDebounce = useDebounce(searchFilter, 300);
    const [viewAll, setViewAll] = useState(false);
    const [statusFilters, setStatusFilters] = useState([]);
    const [filters, setFilters] = useState({});
    const [orderBy, setOrderBy] = useState('ci_created_on');
    const [direction, setDirection] = useState('desc');
    const [page, setPage] = useState(1);
    const MAX_PAGE = 20;

    const scrollRef = useRef(null);
    const [showGradient, setShowGradient] = useState(false);

    const excludeBookingOriginIds = useMemo(() => new Set([BookingOrigin.IDS.undefined, BookingOrigin.IDS.webapp, BookingOrigin.IDS.mobileapp]), []);
    const bookingOriginOptions = useMemo(
        () => [
            ...BookingOrigin.getIds()
                .filter((id) => !excludeBookingOriginIds.has(id))
                .map((id) => {
                    const translated = BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18nLang);
                    return { id, name: translated?.[0] ?? '' };
                }),
        ],
        [excludeBookingOriginIds, i18nLang]
    );

    const excludeBookingStatusOptions = useMemo(() => new Set([BookingStatus.IDS.undefined, BookingStatus.IDS.archived]), []);

    const allBookingStatusIds = useMemo(() => BookingStatus.getIds().filter((id) => !excludeBookingStatusOptions.has(id)), [excludeBookingStatusOptions]);

    const bookingStatusOptions = useMemo(
        () =>
            allBookingStatusIds.map((id) => {
                const translated = BookingStatus.getTranslatedName(BookingStatus.status, id, i18nLang);
                let count = 0;
                if (bookingCounts) {
                    count = bookingCounts[`status_${id}`];
                }
                return { id, name: translated?.[0] ?? '', count: count ?? 0 };
            }),
        [allBookingStatusIds, i18nLang, bookingCounts]
    );

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowGradient(el.scrollLeft + el.clientWidth < el.scrollWidth);
    };

    useEffect(() => {
        checkScroll();
        const el = scrollRef.current;
        if (el) {
            el.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
        }
        return () => {
            if (el) el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, []);

    useEffect(() => {
        getBookings({
            searchTerm: searchTermDebounce,
            viewAll,
            statusFilters,
            filters,
            pager: {
                column: orderBy,
                direction,
                page,
                pageSize: MAX_PAGE,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, orderBy, direction, searchTermDebounce, viewAll, statusFilters, filters]);

    const toggleOpenDialog = () => {
        setOpen(!open);
    };

    const onSubmit = (values) => {
        if (!values.fromDateTime || !values.toDateTime) {
            return;
        }

        const fromDateTime = new Date(values?.fromDateTime);
        const toDateTime = new Date(values?.toDateTime);
        const columns = values?.columns?.map((column) => {
            if (column?.name.length === 0 || typeof column?.name !== 'string') {
                return null;
            }

            const columnName = column?.name
                .split(' ')
                .map((word, index) => {
                    if (index === 0) return word.toLowerCase();

                    return word;
                })
                .join('');

            return {
                [columnName]: true,
            };
        });

        DataportService.exportBookings({
            fromDateTime: fromDateTime.toISOString(),
            toDateTime: toDateTime.toISOString(),
            columns,
        });
    };
    const renderStatus = (bookingStatus) => {
        const status = parseInt(bookingStatus);
        const colorLabel = status === 4 ? '#A6ba62' : status === 2 ? '#9bc2d1' : '#E39348 ';
        return <Chip style={{ color: colorLabel, fontWeight: 'bold' }} label={BookingStatus.getTranslatedName(BookingStatus.status, status, i18nLang)} />;
    };

    const prettifyBookings = (bookings = []) => {
        return bookings.map((booking) => {
            return {
                triper: `${booking.firstName} ${booking.lastName}`,
                statusString: renderStatus(booking.status),
                originString: booking?.originId ? BookingOrigin.getTranslatedName(BookingOrigin.types, booking?.originId, i18nLang) : '',
                priceString: `${booking.currency} ${MoneyFormattedUtils(booking.price)}`,
                priceUSDString: booking?.priceUSD ? `${'USD'} ${MoneyFormattedUtils(booking?.priceUSD)}` : '',
                paidPriceString: `${booking.currency} ${MoneyFormattedUtils(booking.paidPrice)}`,
                commissionString: `${booking.currency} ${MoneyFormattedUtils(booking.commission)}`,
                createdOnString: DateFormattedUtils(booking.createdOn, i18nLang, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: booking.timezone || 'UTC',
                }),
                expId: <PreviewExperience id={booking.experienceId} label="name" previewService={PreviewService} />,
                expDetailId: <PreviewExperienceDetail id={booking.experienceDetailId} label="description1" previewService={PreviewService} />,
                dateString: DateFormattedUtils(booking.dateUTC, i18nLang, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: booking.timezone || 'UTC',
                }),
                feeRateString: `${booking.feeRate}%`,
                ...booking,
            };
        });
    };

    const columns = [
        { align: 'left', val: 'id', label: 'ID', column: 'ci_id' },
        { align: 'left', val: 'code', label: translate('bookings.columns.code'), column: 'ci_code' },
        { val: 'triper', label: translate('bookings.columns.triper'), column: 'ci_first_name', style: { minWidth: '180px' } },
        { val: 'email', label: 'Email', column: 'ci_email' },
        { val: 'expId', label: translate('bookings.columns.experience'), style: { minWidth: '120px' } },
        { val: 'expDetailId', label: translate('bookings.columns.experienceDetail'), column: 'ci_experience_detail_id', style: { minWidth: '120px' } },
        { val: 'mobilePhone', label: translate('bookings.columns.phone'), column: 'ci_mobile_phone', style: { minWidth: '130px' } },
        { val: 'dateString', label: translate('bookings.columns.date'), column: 'ci_date_utc', style: { minWidth: '200px' } },
        { val: 'createdOnString', label: translate('bookings.columns.created_on'), column: 'ci_created_on', style: { minWidth: '200px' } },
        { align: 'center', val: 'quantity', label: translate('bookings.columns.quantity'), column: 'ci_quantity' },
        { align: 'right', val: 'priceUSDString', label: translate('bookings.columns.price_usd'), column: 'ci_price_usd', style: { minWidth: '180px' } },
        { align: 'right', val: 'priceString', label: translate('bookings.columns.price'), column: 'ci_price', style: { minWidth: '150px' } },
        { align: 'right', val: 'paidPriceString', label: translate('bookings.columns.paid_amount'), column: 'ci_paid_price', style: { minWidth: '150px' } },
        { align: 'right', val: 'commissionString', label: translate('bookings.columns.commission'), column: 'ci_comission', style: { minWidth: '150px' } },
        { align: 'center', val: 'feeRateString', label: translate('bookings.columns.fee_rate'), column: 'ci_fee_rate', style: { minWidth: '150px' } },
        { align: 'center', val: 'paymentId', label: translate('bookings.columns.payment_id'), column: 'ci_payment_id', style: { minWidth: '100px' } },
        { align: 'left', val: 'originString', label: translate('bookings.columns.origin'), column: 'ci_origin_id', style: { minWidth: '150px' } },
        { align: 'center', val: 'contractId', label: translate('bookings.columns.contract_id'), column: 'ci_contract_id' },
        { align: 'left', val: 'statusString', label: translate('bookings.columns.status'), column: 'ci_status' },
        { align: 'left', val: 'resellerBookingReference', label: translate('bookings.columns.reseller_code'), column: 'ci_reseller_booking_reference', style: { minWidth: '150px' } },
    ];

    const mobileColumns = [
        { align: 'left', val: 'id', label: 'ID', column: 'ci_id' },
        { val: 'code', label: translate('bookings.columns.code'), column: 'ci_code' },
        { val: 'email', label: 'Email', column: 'ci_email' },
        { val: 'expId', label: translate('bookings.columns.experience'), style: { minWidth: '120px' } },
        { val: 'expDetailId', label: translate('bookings.columns.experienceDetail'), column: 'ci_experience_detail_id' },
        { val: 'dateString', label: translate('bookings.columns.date'), column: 'ci_date_utc', style: { minWidth: '200px' } },
        { val: 'createdOnString', label: translate('bookings.columns.created_on'), column: 'ci_created_on', style: { minWidth: '200px' } },
        { align: 'right', val: 'priceUSDString', label: translate('bookings.columns.price_usd'), column: 'ci_price_usd', style: { minWidth: '180px' } },
        { align: 'right', val: 'priceString', label: translate('bookings.columns.price'), column: 'ci_price', style: { minWidth: '150px' } },
        { align: 'right', val: 'paidPriceString', label: translate('bookings.columns.paid_amount'), column: 'ci_paid_price', style: { minWidth: '150px' } },
        { val: 'originString', label: translate('bookings.columns.origin'), column: 'ci_origin_id', style: { minWidth: '150px' } },
        { align: 'left', val: 'statusString', label: translate('bookings.columns.status'), column: 'ci_status' },
        { align: 'left', val: 'resellerBookingReference', label: translate('bookings.columns.reseller_code'), column: 'ci_reseller_booking_reference', style: { minWidth: '200px' } },
    ];

    const handleSort = (column) => {
        if (orderBy === column) {
            setDirection(direction === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setDirection('asc');
        }
    };

    const handleToggle = () => {
        setOpenFilters(!openFilters);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const onChangeSearch = (event) => {
        setSearchFilter(event.target.value);
    };

    const onClick = (event, id) => {
        history.push(`/booking/${id}`);
    };

    const handleViewAll = () =>
        setViewAll((prevSelected) => {
            if (!prevSelected) {
                setStatusFilters([...allBookingStatusIds]);
            } else {
                setStatusFilters([]);
            }
            return !prevSelected;
        });

    const handleStatusFilters = (event, newStatus) => {
        const areArraysEqual = (a, b) => {
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.length === sortedB.length && sortedA.every((val, i) => val === sortedB[i]);
        };

        if (areArraysEqual(newStatus, allBookingStatusIds)) {
            setViewAll(true);
        } else {
            setViewAll(false);
        }

        setStatusFilters(newStatus);
    };

    const FiltersForm = () => {
        const [filtersForm, setFiltersForm] = useState(filters || {});
        const [isChanged, setIsChanged] = useState(false);

        const handleIsChanged = () => {
            if (!isChanged) {
                setIsChanged(true);
            }
        };

        return (
            <Form
                onSubmit={(values) => {
                    setFilters(values);
                    setIsChanged(false);
                    setOpenFilters(false);
                }}
                initialValues={filtersForm}
                render={({ handleSubmit, form }) => (
                    <form id="form" onSubmit={handleSubmit} style={{ width: '100%' }}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Field
                                        id="date"
                                        name="date"
                                        render={({ input, meta }) => (
                                            <DateRangePickerWithInput
                                                {...input}
                                                label={translate('bookings.filters_form.date_label')}
                                                value={{ startDate: filtersForm?.date?.startDate, endDate: filtersForm?.date?.endDate }}
                                                onChange={({ startDate, endDate }) => {
                                                    setFiltersForm((prev) => {
                                                        return {
                                                            ...prev,
                                                            date: { startDate, endDate },
                                                        };
                                                    });
                                                    handleIsChanged();
                                                }}
                                                useUTC
                                            />
                                        )}
                                    />
                                    <Field
                                        id="originIds"
                                        name="originIds"
                                        render={({ input, meta }) => (
                                            <SelectCheckbox
                                                {...input}
                                                values={filtersForm?.originIds || []}
                                                options={bookingOriginOptions}
                                                label={translate('bookings.filters_form.origin_label')}
                                                labelId="source"
                                                style={{ width: '100%' }}
                                                onChange={(newValues) => {
                                                    setFiltersForm((prev) => ({
                                                        ...prev,
                                                        originIds: [...newValues],
                                                    }));
                                                    handleIsChanged();
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid2>

                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Field
                                        id="contractId"
                                        name="contractId"
                                        render={({ input, meta }) => (
                                            <LookUpContract
                                                {...input}
                                                value={filtersForm.contractId}
                                                label={translate('bookings.filters_form.contract_label')}
                                                entityService={EntityService}
                                                initialValue={filtersForm.contractId}
                                                onChange={(id) => {
                                                    setFiltersForm((prev) => ({ ...prev, contractId: id }));
                                                    handleIsChanged();
                                                }}
                                            />
                                        )}
                                    />
                                    <Field
                                        id="experienceId"
                                        name="experienceId"
                                        render={({ input, meta }) => (
                                            <LookUpExperience
                                                {...input}
                                                value={filtersForm.experienceId}
                                                label={translate('bookings.filters_form.experience_label')}
                                                entityService={EntityService}
                                                initialValue={filtersForm.experienceId}
                                                onChange={(id) => {
                                                    setFiltersForm((prev) => ({ ...prev, experienceId: id }));
                                                    handleIsChanged();
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid2>

                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Field
                                        id="experiencerId"
                                        name="experiencerId"
                                        render={({ input, meta }) => (
                                            <LookUpExperiencer
                                                {...input}
                                                value={filtersForm.experiencerId}
                                                label={translate('bookings.filters_form.experiencer_label')}
                                                entityService={EntityService}
                                                initialValue={filtersForm.experiencerId}
                                                onChange={(id) => {
                                                    setFiltersForm((prev) => ({ ...prev, experiencerId: id }));
                                                    handleIsChanged();
                                                }}
                                            />
                                        )}
                                    />
                                    <Field
                                        id="hostId"
                                        name="hostId"
                                        render={({ input, meta }) => (
                                            <LookUpHost
                                                {...input}
                                                value={filtersForm.hostId}
                                                label="Host"
                                                entityService={EntityService}
                                                initialValue={filtersForm.hostId}
                                                onChange={(id) => {
                                                    setFiltersForm((prev) => ({ ...prev, hostId: id }));
                                                    handleIsChanged();
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid2>
                        </Grid2>

                        <Grid2 mt={2} xs={12}>
                            <Box display="flex" gap={2} flexWrap="wrap" justifyContent={isMobile ? 'space-between' : 'flex-end'}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RestartAlt />}
                                    onClick={() => {
                                        if (Object.keys(filtersForm)?.length > 0) {
                                            setIsChanged(true);
                                        } else {
                                            setIsChanged(false);
                                        }
                                        setFiltersForm({});
                                        form.reset();
                                    }}
                                    className={classes.button}
                                    sx={{
                                        flex: isMobile ? 1 : 'unset',
                                        minWidth: isMobile ? 0 : 'auto',
                                    }}>
                                    {translate('bookings.filters_form.reset_button')}
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={!isChanged}
                                    sx={{
                                        flex: isMobile ? 1 : 'unset',
                                        minWidth: isMobile ? 0 : 'auto',
                                        fontWeight: 'bold',
                                    }}>
                                    {translate('bookings.filters_form.apply_button')}
                                </Button>
                            </Box>
                        </Grid2>
                    </form>
                )}
            />
        );
    };

    return (
        <Layout contentClassName={classes.content}>
            <Title value={translate('bookings.title')} />

            <Grid2 container spacing={1}>
                <Grid2 alignSelf="center">
                    <StyledToggleButton value={viewAll} selected={viewAll} onChange={handleViewAll} aria-label="all booking status">
                        <Box display="flex" alignItems="center" gap={1}>
                            {translate('bookings.all_bookings')}
                            <Box
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#9e9e9e',
                                    borderRadius: '12px',
                                    px: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                }}>
                                {bookingCounts?.total}
                            </Box>
                        </Box>
                    </StyledToggleButton>
                </Grid2>

                <Grid2>
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                        <Box
                            ref={scrollRef}
                            sx={{
                                overflowX: 'auto',
                                display: 'flex',
                                overflowY: 'hidden',
                            }}>
                            <StyledToggleButtonGroup value={statusFilters} onChange={handleStatusFilters} aria-label="booking status">
                                {bookingStatusOptions.map((bkgStatus) => (
                                    <ToggleButton key={bkgStatus.id} value={bkgStatus.id} aria-label={bkgStatus.name}>
                                        <Box display="flex" alignItems="center" gap={1} width="max-content">
                                            {bkgStatus.name}
                                            <Box
                                                sx={{
                                                    backgroundColor: 'white',
                                                    color: '#9e9e9e',
                                                    borderRadius: '12px',
                                                    px: 1,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                }}>
                                                {bkgStatus?.count}
                                            </Box>
                                        </Box>
                                    </ToggleButton>
                                ))}
                            </StyledToggleButtonGroup>
                        </Box>

                        {showGradient && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    height: '100%',
                                    width: 60,
                                    background: 'linear-gradient(to left, white, transparent)',
                                    display: 'flex',
                                }}></Box>
                        )}
                    </Box>
                </Grid2>
            </Grid2>

            <Grid2 container justifyContent={{ xs: 'flex-start', md: 'space-between' }} alignItems="center" flexWrap={{ xs: 'wrap', md: 'nowrap' }} spacing={2} my={2}>
                <Button variant="outlined" className={classes.button} sx={{ order: { xs: 2, md: 1 } }} startIcon={<FilterList />} onClick={handleToggle}>
                    {translate('bookings.filters_button')}
                </Button>

                <TextInput
                    input={{
                        slotProps: {
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            },
                        },
                        sx: {
                            order: { xs: 1, md: 2 },
                        },
                    }}
                    meta={{ error: null, touched: null }}
                    onChange={onChangeSearch}
                    value={searchFilter}
                    label={translate('bookings.search_label')}
                    placeholder={translate('bookings.search_label')}
                />
                <Button variant="outlined" className={classes.button} sx={{ order: { xs: 2, md: 3 } }} startIcon={<Download />} onClick={toggleOpenDialog}>
                    {translate('bookings.export_button')}
                </Button>
            </Grid2>

            <Grid2 mb={2}>
                <ResponsivePanel title={translate('bookings.filters_button')} open={openFilters} handleToggle={handleToggle}>
                    <FiltersForm />
                </ResponsivePanel>
            </Grid2>

            <div>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <Fade in={!loading}>
                        <div>
                            <BasicTable size="small" columns={!isMobile ? columns : mobileColumns} handleSort={handleSort} orderBy={orderBy} direction={direction} values={prettifyBookings(bookings)} onClick={onClick} />
                            <br />
                            <Grid2 container justifyContent="center" alignItems="center">
                                <Grid2>{total / MAX_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePageChange} totalItems={total} itemsPerPage={MAX_PAGE} />}</Grid2>
                            </Grid2>
                        </div>
                    </Fade>
                )}
            </div>
            <AlertDialog open={open} handleClose={toggleOpenDialog} hideActions dialogTitle={translate('bookings.export_csv')} dialogContent={<ExportForm onSubmit={onSubmit} onClose={toggleOpenDialog} isRangePicker={true} />} maxWidth="sm" fullWidth />
        </Layout>
    );
};

BookingsPage.displayName = 'BookingsPage';

export default BookingsPage;
